import { useMemo } from 'react';
import { DSResultsResponseResultsItem } from '@rossum/mdh-api-client';
import { Row, ROW_STATUS, RowEdit, RowEdits } from './types';

type Props = {
  data: DSResultsResponseResultsItem[] | undefined;
  edits: RowEdits;
};

export const useRows = ({ edits, data }: Props) => {
  // Base rows in server order. Recomputed only when the page data changes —
  // crucially, each row object keeps a stable reference across renders so the
  // grid can skip re-rendering rows that aren't affected by an edit.
  const baseRows = useMemo<Row[]>(() => {
    if (!data) return [];

    return data.map(row => ({
      ...row,
      _id: String(row['_id']),
      [ROW_STATUS]: 'idle',
    }));
  }, [data]);

  const newRows = useMemo<Row[]>(
    () =>
      [...edits]
        .filter(
          (entry): entry is [string, Extract<RowEdit, { kind: 'new' }>] =>
            entry[1].kind === 'new'
        )
        .map(([id, edit]) => ({
          ...edit.changes,
          _id: id,
          [ROW_STATUS]: 'new' as const,
        })),
    [edits]
  );

  const rows = useMemo<Row[]>(() => {
    if (edits.size === 0) return baseRows;

    // Only the edited/deleted rows get a new object; untouched rows pass through
    // by reference. The page arrives already sorted (server-side sort +
    // pagination), so we keep that order rather than re-sorting here.
    const serverRows = baseRows.map((row): Row => {
      const edit = edits.get(row._id);
      if (!edit || edit.kind === 'new') return row;

      return edit.kind === 'deleted'
        ? { ...row, [ROW_STATUS]: 'deleted' }
        : { ...row, ...edit.changes, [ROW_STATUS]: 'edited' };
    });

    return [...newRows, ...serverRows];
  }, [edits, baseRows, newRows]);

  return { rows };
};
